// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useDeferredValue, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock, faXmark } from "@fortawesome/free-solid-svg-icons";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import { blogPosts, getBlogPostBySlug } from "../../consts/blogPosts";
import { kanjiTextbookOrder } from "../../consts/kanjiTextbookOrder";
import { kanjiDetails } from "../../consts/kanjiDetails";
import { estimateReadingMinutes } from "../../utils/blogReadingTime";
import { buildKanjiSearchIndex, searchKanjiIndex } from "../../utils/kanjiSearch";
import Seo from "../Seo/SeoComponent";
import TableCell from "../ui/TableCell/TableCell";
import JapaneseWordLink from "./JapaneseWordLink";
import BackLink from "../ui/BackLink/BackLink";
import BlogSubscribePrompt from "../NewsletterForm/BlogSubscribePrompt";
import { useAuth } from "../../context/AuthContext";
import { useBlogReadProgress } from "../../hooks/useBlogReadProgress";
import { useActiveReadingTime } from "../../hooks/useActiveReadingTime";
import styles from "./blogPostPage.module.scss";

const MS_PER_MINUTE = 60_000;
const SITE_URL = "https://nihongotech.teqseq.com";

// Article + BreadcrumbList structured data for a blog post - only fields
// genuinely present on the page (no invented ratings/reviews/author bios).
// NihongoTech is both author and publisher: posts carry no individual
// byline in the UI, so claiming a named human author here would not match
// what a reader actually sees.
function buildBlogPostJsonLd(post, path, blogPath, homePath) {
    const url = `${SITE_URL}${path}`;
    const inLanguage = post.lang === "pt-PT" ? "pt-PT" : "en";
    const organization = { "@id": `${SITE_URL}/#organization` };
    const article = {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${url}#article`,
        headline: post.title,
        description: post.excerpt,
        datePublished: post.date,
        dateModified: post.date,
        inLanguage,
        mainEntityOfPage: url,
        url,
        author: organization,
        publisher: organization,
        ...(post.tags?.length ? { keywords: post.tags.join(", ") } : {}),
    };
    const breadcrumb = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "NihongoTech", item: `${SITE_URL}${homePath}` },
            { "@type": "ListItem", position: 2, name: inLanguage === "pt-PT" ? "Blogue" : "Blog", item: `${SITE_URL}${blogPath}` },
            { "@type": "ListItem", position: 3, name: post.title, item: url },
        ],
    };
    return [article, breadcrumb];
}

function formatDate(dateString, locale = "en-US") {
    return new Date(`${dateString}T00:00:00`).toLocaleDateString(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

const jlptLevels = [5, 4, 3, 2, 1];

// Surfaces kanji covered by a common teaching order (see kanjiTextbookOrder)
// first, in that order, ahead of everything else in the jōyō list.
const textbookPriority = new Map(kanjiTextbookOrder.map((symbol, index) => [symbol, index]));

const identity = (path) => path;
const englishMeaning = (symbol, meaning) => meaning;

function KanjiMeaningGrid({ items, localize = identity, meaningFor = englishMeaning }) {
    const { t } = useTranslation("blogPage");
    // Empty set means "show every level" - the same state the "All" button sets explicitly.
    const [selectedLevels, setSelectedLevels] = useState(() => new Set([5]));
    const [query, setQuery] = useState("");
    const inputRef = useRef(null);
    const inputId = useId();
    const filterLabelId = useId();
    // Typing stays instant; the (potentially 3,000-tile) grid catches up at low priority.
    const deferredQuery = useDeferredValue(query).trim();
    const isSearching = deferredQuery !== "";

    // Textbook order once per list; level and search filters below preserve it.
    const sortedItems = useMemo(
        () =>
            [...items].sort((a, b) => {
                const priorityA = textbookPriority.has(a.symbol) ? textbookPriority.get(a.symbol) : Infinity;
                const priorityB = textbookPriority.has(b.symbol) ? textbookPriority.get(b.symbol) : Infinity;
                return priorityA - priorityB;
            }),
        [items]
    );
    const searchItems = useMemo(
        () => sortedItems.map((item) => {
            const details = kanjiDetails[item.symbol];
            return {
                ...item,
                readings: [...(details?.onyomi ?? []), ...(details?.kunyomi ?? []), ...(details?.examples ?? []).map((example) => example.reading)],
            };
        }),
        [sortedItems]
    );
    const searchIndex = useMemo(() => buildKanjiSearchIndex(searchItems, meaningFor), [searchItems, meaningFor]);
    const levelCounts = useMemo(
        () => jlptLevels.map((level) => items.filter((item) => item.jlptLevel === level).length),
        [items]
    );
    const filtered = useMemo(() => {
        // While searching, look across every level so a match is never hidden by the N-level filter.
        if (isSearching) return searchKanjiIndex(searchIndex, deferredQuery);
        return selectedLevels.size === 0
            ? sortedItems
            : sortedItems.filter((item) => selectedLevels.has(item.jlptLevel));
    }, [isSearching, deferredQuery, searchIndex, sortedItems, selectedLevels]);

    const clearSearch = () => {
        setQuery("");
        inputRef.current?.focus();
    };

    // Toggling a level adds/removes it from the selection; letting the last one go
    // falls through to the empty-set "show everything" state, same as the All button.
    const toggleLevel = (level) => {
        setSelectedLevels((current) => {
            const next = new Set(current);
            if (next.has(level)) {
                next.delete(level);
            } else {
                next.add(level);
            }
            return next;
        });
    };

    const handleKeyDown = (event) => {
        // Escape also cancels an IME composition; leave that to the IME.
        if (event.key === "Escape" && !event.nativeEvent.isComposing && query !== "") {
            event.preventDefault();
            setQuery("");
        }
    };

    let statusText = "";
    if (isSearching) {
        statusText = filtered.length === 0 ? t("post.search.noResults") : t("post.search.found", { count: filtered.length });
    }

    return (
        <div>
            <div className={styles.kanjiSearch}>
                <label htmlFor={inputId} className={styles.visuallyHidden}>
                    {t("post.search.label")}
                </label>
                <input
                    id={inputId}
                    ref={inputRef}
                    type="search"
                    className={styles.kanjiSearchInput}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={t("post.search.placeholder")}
                    inputMode="search"
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                />
                {query !== "" && (
                    <button
                        type="button"
                        className={styles.kanjiSearchClear}
                        onClick={clearSearch}
                        aria-label={t("post.search.clear")}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                )}
            </div>
            <span id={filterLabelId} className={styles.visuallyHidden}>
                {t("post.jlptFilterLabel")}
            </span>
            <div className={styles.jlptFilterRow} role="group" aria-labelledby={filterLabelId}>
                <button
                    type="button"
                    className={`${styles.jlptFilterButton} ${!isSearching && selectedLevels.size === 0 ? styles.jlptFilterButtonActive : ""}`}
                    aria-pressed={!isSearching && selectedLevels.size === 0}
                    onClick={() => setSelectedLevels(new Set())}
                    disabled={isSearching}
                >
                    {t("post.all", { count: items.length })}
                </button>
                {jlptLevels.map((level, levelIndex) => (
                    <button
                        key={level}
                        type="button"
                        className={`${styles.jlptFilterButton} ${!isSearching && selectedLevels.has(level) ? styles.jlptFilterButtonActive : ""}`}
                        aria-pressed={!isSearching && selectedLevels.has(level)}
                        onClick={() => toggleLevel(level)}
                        disabled={isSearching}
                    >
                        N{level} ({levelCounts[levelIndex]})
                    </button>
                ))}
            </div>
            <p className={styles.kanjiSearchStatus} role="status" aria-live="polite">
                {isSearching && (
                    <>
                        <span>{statusText}</span>
                        <span className={styles.kanjiSearchNote}> · {t("post.search.allLevels")}</span>
                    </>
                )}
            </p>
            {isSearching && filtered.length === 0 ? (
                <p className={styles.kanjiSearchEmpty}>{t("post.search.empty", { query: deferredQuery })}</p>
            ) : (
                <div className={styles.kanjiMeaningGrid}>
                    {filtered.map((item) => (
                        <Link
                            key={item.symbol}
                            to={localize(`/kanji/${encodeURIComponent(item.symbol)}`)}
                            className={styles.kanjiMeaningTile}
                        >
                            <span className={styles.kanjiMeaningGlyph}>{item.symbol}</span>
                            <span className={styles.kanjiMeaningText}>{meaningFor(item.symbol, item.meaning)}</span>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}

function ContentBlock({ block, index, localize = identity, meaningFor = englishMeaning }) {
    if (block.image) {
        const { src, alt, caption } = block.image;
        return (
            <figure key={index} className={styles.imageFigure}>
                <img className={styles.contentImage} src={src} alt={alt} />
                {caption && <figcaption className={styles.imageCaption}>{caption}</figcaption>}
            </figure>
        );
    }

    if (block.callout) {
        return (
            <aside key={index} className={styles.callout} role="note">
                {block.callout}
            </aside>
        );
    }

    if (block.heading) {
        return (
            // aria-label keeps the heading's accessible name identical to its visible
            // text: the "name from content" algorithm would otherwise insert a space
            // around the nested save button (e.g. "...五段: ..." -> "...五段 : ...").
            <h2 key={index} className={styles.sectionHeading} aria-label={block.heading}>
                <JapaneseWordLink text={block.heading} />
            </h2>
        );
    }

    if (block.kanjiMeaningGrid) {
        return <KanjiMeaningGrid key={index} items={block.kanjiMeaningGrid} localize={localize} meaningFor={meaningFor} />;
    }

    if (block.table) {
        const { caption, headers, rows, rowHeaders = true } = block.table;
        return (
            <figure key={index} className={styles.tableFigure}>
                <div className={styles.tableScroll} tabIndex={0} role="region" aria-label={caption}>
                    <table className={styles.table}>
                        <caption className={styles.tableCaption}>{caption}</caption>
                        <thead>
                            <tr>
                                {headers.map((header) => (
                                    <th key={header} scope="col">
                                        {header}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, rowIndex) => (
                                <tr key={rowIndex}>
                                    {row.map((cell, cellIndex) =>
                                        rowHeaders && cellIndex === 0 ? (
                                            <th key={cellIndex} scope="row">
                                                <TableCell cell={cell} renderText={(text, key) => <JapaneseWordLink key={key} text={text} />} />
                                            </th>
                                        ) : (
                                            <td key={cellIndex}>
                                                <TableCell cell={cell} renderText={(text, key) => <JapaneseWordLink key={key} text={text} />} />
                                            </td>
                                        )
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </figure>
        );
    }

    if (block.list) {
        return (
            <ul key={index} className={styles.list}>
                {block.list.map((item) => (
                    <li key={item}>
                        <JapaneseWordLink text={item} />
                    </li>
                ))}
            </ul>
        );
    }

    if (block.parts) {
        return (
            <p key={index} className={styles.paragraph}>
                {block.parts.map((part, partIndex) => {
                    if (typeof part === "string") {
                        return <JapaneseWordLink key={partIndex} text={part} />;
                    }
                    if (part.href) {
                        return (
                            <a
                                key={partIndex}
                                href={part.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.inlineLink}
                            >
                                {part.label}
                            </a>
                        );
                    }
                    return (
                        <Link key={partIndex} to={localize(part.to)} className={styles.inlineLink}>
                            {part.label}
                        </Link>
                    );
                })}
            </p>
        );
    }

    return (
        <p key={index} className={styles.paragraph}>
            <JapaneseWordLink text={block.text} />
        </p>
    );
}

export function blogPostAlternates(slug) {
    const post = getBlogPostBySlug(slug);
    if (!post) return [];
    const englishTwin = blogPosts.find((candidate) => candidate.ptSlug === slug);
    if (post.lang === "pt-PT") {
        return englishTwin
            ? [
                  { hrefLang: "en", path: `/blog/${englishTwin.slug}` },
                  { hrefLang: "pt-PT", path: `/blog/${slug}` },
              ]
            : [];
    }
    return [
        { hrefLang: "en", path: `/blog/${slug}` },
        { hrefLang: "pt-PT", path: post.ptSlug ? `/blog/${post.ptSlug}` : `/pt/blog/${slug}` },
    ];
}

export function BlogPostNotFound({ path, blogPath, localeLang }) {
    const { t } = useTranslation("blogPage");
    return (
        <div className={styles.page}>
            <Seo title={t("post.notFoundTitle")} path={path} noindex lang={localeLang} />
            <Header />

            <main className={styles.main}>
                <section className={styles.container}>
                    <h1 className={styles.title}>{t("post.notFoundTitle")}</h1>
                    <p className={styles.description}>{t("post.notFoundDescription")}</p>
                    <BackLink to={blogPath} label={t("post.backToBlog")} />
                </section>
            </main>

            <Footer />
        </div>
    );
}

export function BlogPostView({ post, path, blogPath = "/blog", localize = identity, meaningFor = englishMeaning, alternates = [] }) {
    const { t } = useTranslation("blogPage");
    const readingMinutes = estimateReadingMinutes(post.content);
    const { status: authStatus } = useAuth();
    const { progress: readProgress, setRead, syncStatus: readSyncStatus } = useBlogReadProgress();
    const alreadyRead = readProgress[post.slug] === "read";
    // Gated on sign-in (progress is account-only) and on the sync having
    // settled, so a row that's already "read" on another device isn't
    // briefly re-tracked here while it's still loading.
    const trackingEnabled = authStatus === "authenticated" && readSyncStatus !== "loading" && !alreadyRead;
    useActiveReadingTime(post.slug, readingMinutes * MS_PER_MINUTE, trackingEnabled, () => setRead(post.slug));
    const homePath = blogPath.startsWith("/pt") ? "/pt" : "/";
    return (
        <div className={styles.page}>
            <Seo
                title={post.title}
                description={post.excerpt}
                path={path}
                lang={post.lang}
                alternates={alternates}
                keywords={post.keywords}
                jsonLd={buildBlogPostJsonLd(post, path, blogPath, homePath)}
            />
            <Header />

            <main className={styles.main}>
                <article className={styles.container}>
                    <BackLink to={blogPath} label={t("post.backToBlog")} />

                    <header className={styles.postHeader}>
                        <div className={styles.postMeta}>
                            <time dateTime={post.date}>{formatDate(post.date, post.lang)}</time>
                            <span className={styles.postReadingTime}>
                                <FontAwesomeIcon icon={faClock} aria-hidden="true" className={styles.readingTimeIcon} />
                                {t("post.readingTime", { count: readingMinutes })}
                            </span>
                            {post.tags?.map((tag) => (
                                <Link
                                    key={tag}
                                    to={`${blogPath}?tag=${encodeURIComponent(tag)}`}
                                    className={styles.postTag}
                                >
                                    {t(`tags.${tag}`, { defaultValue: tag })}
                                </Link>
                            ))}
                        </div>
                        <h1 className={styles.title}>{post.title}</h1>
                    </header>

                    <div className={styles.body}>
                        {post.content.map((block, index) => (
                            <ContentBlock key={index} block={block} index={index} localize={localize} meaningFor={meaningFor} />
                        ))}
                    </div>

                    <BlogSubscribePrompt />
                </article>
            </main>

            <Footer />
        </div>
    );
}

function BlogPostPage() {
    const { slug } = useParams();
    const post = getBlogPostBySlug(slug);

    if (!post) {
        return <BlogPostNotFound path={`/blog/${slug}`} blogPath="/blog" />;
    }

    return <BlogPostView post={post} path={`/blog/${post.slug}`} alternates={blogPostAlternates(post.slug)} />;
}

export default BlogPostPage;
